/**
 * castGeometry — one description of what a cast DRAWS, for the caster and for
 * everyone watching (plan 29 F5, lane BV 2026-10-11).
 *
 * Melee swings used to be drawn only on the caster's screen, and remote AOEs
 * replayed the TOML `cast_effect` at the caster with no offset and no size
 * while the local module placed Meteor 200 px ahead, Blizzard 150 px, Rain of
 * Arrows 200 px. The two sides read two different descriptions.
 *
 * Now there is one:
 *
 *  - `CastGeometry` is the visible geometry of an ability: the swing (its
 *    sweep; the reach and cone come from `resolveMeleeCone`, the same numbers
 *    the server hits with), the area (radius, forward offset, zone effect,
 *    wind-up, ticks), the placed zone, the cone, the line, the caster aura,
 *    the orbit, the dash and the caster beat.
 *  - It is DESCRIBED from the ability's own builder configs at build time
 *    (`describeCastGeometry`, stored on the registry metadata) — the very
 *    configs the local behaviours run on.
 *  - `castVisualPlan` (castVisualPlan.ts) turns a geometry plus a caster pose
 *    into the exact EventBus `vfx:*` emits. The local behaviours emit their
 *    visuals through it; the remote player emits the same plan from the
 *    replicated cast event, anchored on the remote body and facing its way.
 *  - Production boot registers only the local player's class, so an observer
 *    cannot build another class's modules. The remote side therefore reads a
 *    generated snapshot of the descriptions (`castGeometry.gen.ts`, every
 *    player-obtainable ability). The contract test regenerates and compares
 *    it, so a module edit that changes what a cast draws fails until the
 *    snapshot is refreshed (`make cast-geometry`).
 */
import { CAST_GEOMETRY_SNAPSHOT } from './castGeometry.gen';
import type { NovaLook } from './novaLaunches';
import type { ProjectileGeometry } from './projectileGeometry';

/** The sweep of a melee swing (right-facing degrees; the renderer mirrors). */
export interface SwingGeometry {
  arcAngle: number;
  arcSpread: number;
  lineWidth: number;
  durationMs: number;
  lineColor?: number;
}

/** A ground area (AOEBehavior): centred `forwardPx` ahead of the body centre. */
export interface AreaGeometry {
  radius: number;
  forwardPx: number;
  effectId?: string;
  windUpEffectId?: string;
  windUpMs: number;
  ticks: number;
  intervalMs: number;
  /** The ring's window when it is not wind-up + ticks (an area heal's 600 ms splash). */
  markerMs?: number;
}

/** A placed zone, trap or totem (SummonBehavior). */
export interface ZoneGeometry {
  kind: 'trap' | 'totem' | 'zone';
  radius: number;
  forwardPx: number;
  durationMs: number;
  effectId?: string;
  triggerEffectId?: string;
  trigger: 'proximity' | 'timed' | 'immediate';
  triggerDelayMs: number;
}

/** A wedge from the body centre (withConeAOE). */
export interface ConeGeometry {
  angle: number;
  distance: number;
  effectId?: string;
}

/** A line ahead of the body centre (withLineAOE). */
export interface LineGeometry {
  length: number;
  width: number;
  effectId?: string;
}

/** A glow that rides the caster (withCasterAura). */
export interface CasterAuraGeometry {
  effectId: string;
  durationMs: number;
  color?: number;
}

/** Stars orbiting the caster before they fire (Lucky Seven). */
export interface OrbitGeometry {
  numStars: number;
  radius: number;
  durationMs: number;
}

/** A dash (DashBehavior) — remote travel/landing is drawn by the `dashed` broadcast. */
export interface DashGeometry {
  distance: number;
  travelMs: number;
  originEffectId?: string;
  destinationEffectId?: string;
  /** Authored movement presentation, independent of renderer/runtime objects. */
  mode?: 'dash' | 'leap' | 'pull' | 'run';
  horizontalPolicy?: 'distance' | 'until-landed';
  trailEffectId?: string;
  landingEffectId?: string;
  ghosts?: {
    kind: 'ghosts'; tint: number; edgeTint?: number; count: number; intervalMs: number;
    lifeMs: number; startAlpha: number; blend: 'add' | 'normal';
  };
}

/** The one-shot `withVFX` beat at the body centre on non-projectile casts. */
export interface BeatGeometry {
  effectId: string;
  durationMs: number;
  color?: number;
  scale?: number;
}

/** A multi-shot volley (MultiProjectileBehavior): `count` shots `staggerMs` apart. */
export interface VolleyGeometry {
  count: number;
  staggerMs: number;
}

export interface CastGeometry {
  /** The caster's wind-up: the local behaviours (and so every visual) run after it. */
  castDelayMs?: number;
  /** The tell the caster's screen draws on the caster through that wind-up
   *  (plan 29 lane BZ: observers saw nothing for Howl of Terror's 2 s). */
  castDelayEffectId?: string;
  swing?: SwingGeometry;
  area?: AreaGeometry;
  zone?: ZoneGeometry;
  cone?: ConeGeometry;
  line?: LineGeometry;
  casterAura?: CasterAuraGeometry;
  orbit?: OrbitGeometry;
  dash?: DashGeometry;
  beat?: BeatGeometry;
  volley?: VolleyGeometry;
  /** A nova / fan of visual projectiles from the body centre (NovaProjectileBehavior). */
  nova?: NovaLook;
  /**
   * The caster's final projectile (projectileGeometry.ts) — added when the
   * ability registers, from the metadata its projectile behaviours fire
   * from; observers launch it through the remote projectile path.
   */
  projectile?: ProjectileGeometry;
}

/** True when the geometry draws something the remote plan renders. */
export function hasRemoteVisuals(g: CastGeometry | undefined): g is CastGeometry {
  return !!g && !!(g.castDelayEffectId || g.swing || g.area || g.zone || g.cone || g.line || g.casterAura || g.orbit || g.beat || g.nova);
}

/** Every effect id the geometry spawns (`vfx:spawn`) — for de-duplicating other plays. */
export function geometryEffectIds(g: CastGeometry | undefined): string[] {
  if (!g) return [];
  return [
    g.castDelayEffectId, g.beat?.effectId, g.area?.effectId, g.area?.windUpEffectId, g.zone?.effectId, g.zone?.triggerEffectId,
    g.cone?.effectId, g.line?.effectId,
  ].filter((id): id is string => typeof id === 'string');
}

/** The point a forward offset lands on, from the body centre and facing. */
export function forwardOf(body: { x: number; y: number }, facingRight: boolean, forwardPx: number): { x: number; y: number } {
  return { x: body.x + (facingRight ? forwardPx : -forwardPx), y: body.y };
}

/**
 * The geometry an observer draws for `abilityId`: the generated snapshot of the
 * ability's own description. Undefined for an ability outside the snapshot
 * (not player-obtainable) — the caller keeps its content-only fallback.
 */
export function resolveCastGeometry(abilityId: string | undefined): CastGeometry | undefined {
  if (!abilityId) return undefined;
  return (CAST_GEOMETRY_SNAPSHOT as Record<string, CastGeometry | undefined>)[abilityId];
}
